import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import clsx from "clsx";
import type { POI } from "@poi-planner/shared";
import { poiMapsUrl } from "../utils/poiMaps";
import { Button } from "./Button";
import { LinkifiedText } from "./LinkifiedText";
import { CalendarAnchoredPopover } from "./calendar/CalendarActionToolbar";

const NOTE_OPEN_DELAY_MS = 250;
const NOTE_CLOSE_DELAY_MS = 200;
/** Clears the card padding and border beside the notes anchor. */
const NOTE_POPOVER_OFFSET_PX = 20;

/** Stops a link or control inside a card from starting the card's drag or
 * firing its click (selection). */
function stopCardGesture(event: SyntheticEvent) {
  event.stopPropagation();
}

function DetailLabel({
  icon,
  children,
}: {
  icon: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-0.5 flex items-center gap-1 text-2xs font-semibold text-tertiary">
      <span className="material-symbols-outlined text-sm" aria-hidden>
        {icon}
      </span>
      {children}
    </div>
  );
}

/**
 * Clamped notes. When the clamp hides text, hovering the notes (or using the
 * "Full note" control on touch/keyboard) opens a popout with the whole note.
 */
function PoiNotes({ poiName, notes }: { poiName: string; notes: string }) {
  const notesRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    const measure = () => setTruncated(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [notes]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);
  const schedule = useCallback(
    (next: boolean) => {
      clearTimer();
      timerRef.current = setTimeout(
        () => setOpen(next),
        next ? NOTE_OPEN_DELAY_MS : NOTE_CLOSE_DELAY_MS,
      );
    },
    [clearTimer],
  );
  useEffect(() => clearTimer, [clearTimer]);

  // Touch has no hover: a tap elsewhere closes the popout.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (notesRef.current?.contains(target)) return;
      if (toggleRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  return (
    <div>
      <div
        ref={notesRef}
        className="line-clamp-4 whitespace-pre-line break-words text-xs leading-relaxed text-primary"
        onMouseEnter={truncated ? () => schedule(true) : undefined}
        onMouseLeave={truncated ? () => schedule(false) : undefined}
      >
        <LinkifiedText text={notes} />
      </div>
      {truncated && (
        <Button
          ref={toggleRef}
          variant="unstyled"
          className="mt-0.5 text-2xs font-semibold text-info hover:underline"
          aria-expanded={open}
          aria-haspopup="dialog"
          onPointerDown={stopCardGesture}
          onClick={(event) => {
            event.stopPropagation();
            clearTimer();
            setOpen((current) => !current);
          }}
        >
          {open ? "Hide full note" : "Full note"}
        </Button>
      )}
      {open && (
        <CalendarAnchoredPopover
          anchorRef={notesRef}
          role="dialog"
          aria-label={`Notes for ${poiName}`}
          offset={NOTE_POPOVER_OFFSET_PX}
          className="w-72 rounded-xl border border-border bg-surface shadow-lg"
          onMouseEnter={clearTimer}
          onMouseLeave={() => schedule(false)}
        >
          <div ref={popoverRef} className="max-h-96 overflow-y-auto p-3">
            <DetailLabel icon="notes">Notes</DetailLabel>
            <div className="whitespace-pre-line break-words text-xs leading-relaxed text-primary">
              <LinkifiedText text={notes} />
            </div>
          </div>
        </CalendarAnchoredPopover>
      )}
    </div>
  );
}

interface PoiDetailsProps {
  poi: POI;
  /** Offers an "Add notes" prompt when the POI has none. */
  canEdit?: boolean;
  onEdit?: () => void;
  /**
   * Clamp long notes behind a "Full note" popout. Containers that scroll
   * their own content (the phone results card) show the whole note instead.
   */
  clampNotes?: boolean;
  className?: string;
}

/** Sources worth linking to; ones without a URL are left out of the card. */
function linkedSources(poi: POI): Array<{ text: string; url: string }> {
  return poi.sources.flatMap((source) => {
    const url = source.url?.trim();
    return url ? [{ text: source.text, url }] : [];
  });
}

/** Notes, linked recommendation sources, and a Google Maps link for a POI. */
export function PoiDetails({
  poi,
  canEdit = false,
  onEdit,
  clampNotes = true,
  className,
}: PoiDetailsProps) {
  const notes = poi.notes?.trim();
  const sources = linkedSources(poi);
  const mapsUrl = poiMapsUrl(poi);

  return (
    <div className={clsx("flex flex-col gap-2.5", className)}>
      {notes && clampNotes ? (
        <PoiNotes poiName={poi.name} notes={notes} />
      ) : notes ? (
        <div className="whitespace-pre-line break-words text-xs leading-relaxed text-primary">
          <LinkifiedText text={notes} />
        </div>
      ) : (
        canEdit &&
        onEdit && (
          <Button
            variant="unstyled"
            className="self-start text-xs font-medium text-tertiary hover:text-info"
            icon="edit_note"
            onPointerDown={stopCardGesture}
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
          >
            Add notes
          </Button>
        )
      )}

      {sources.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs">
          {sources.map((source, index) => (
            <li key={`${source.url}-${index}`} className="min-w-0">
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                title={source.url}
                className="group/link inline-flex max-w-full items-center gap-1 text-info no-underline"
                onPointerDown={stopCardGesture}
                onClick={stopCardGesture}
              >
                <span
                  className="material-symbols-outlined icon-match-text shrink-0"
                  aria-hidden
                >
                  open_in_new
                </span>
                <span className="truncate group-hover/link:underline">
                  {source.text.trim() || source.url}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group/link inline-flex items-center gap-1 self-start text-xs font-semibold text-info no-underline"
          onPointerDown={stopCardGesture}
          onClick={stopCardGesture}
        >
          <span
            className="material-symbols-outlined icon-match-text shrink-0"
            aria-hidden
          >
            map
          </span>
          <span className="group-hover/link:underline">
            Open in Google Maps
          </span>
        </a>
      )}
    </div>
  );
}
